<template>
  <v-dialog v-model="aperto" width="1700" scrollable>
    <template v-slot:activator="{ on }">
      <slot name="activator" :on="on">
        <v-btn outlined color="amber darken-2" v-on="on">
          <v-icon left>mdi-calendar</v-icon>Calendario
        </v-btn>
      </slot>
    </template>

    <!-- 90vh e non oltre: è il tetto che v-dialog impone al contenuto, e
         sforarlo fa comparire una seconda barra di scorrimento. -->
    <v-card height="90vh">
      <v-card-title class="grey--text text-subtitle-1 pb-2">
        Calendario consegne

        <!-- Due gruppi distinti: a sinistra quale vista, a destra dove si
             sta guardando. "Oggi" appartiene al secondo. -->
        <v-btn-toggle v-model="tipo" mandatory dense class="ml-6" borderless>
          <v-btn small value="month" text>Mese</v-btn>
          <v-btn small value="week" text>Settimana</v-btn>
        </v-btn-toggle>

        <v-divider vertical class="mx-5 my-2" />

        <v-btn icon small @click="$refs.calendario.prev()">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
        <v-btn small outlined color="grey darken-1" class="mx-1" @click="oggi">
          <v-icon left small>mdi-calendar-today</v-icon>Oggi
        </v-btn>
        <v-btn icon small @click="$refs.calendario.next()">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
        <span class="ml-4 text-subtitle-1 black--text">{{ titolo }}</span>

        <v-spacer />

        <v-checkbox
          v-model="mostra_consegne"
          dense
          hide-details
          class="mt-0 mr-4"
          color="blue"
          label="Consegne"
        />
        <v-checkbox
          v-model="mostra_ritiri"
          dense
          hide-details
          class="mt-0 mr-4"
          color="green"
          label="Ritiri"
        />
        <v-checkbox
          v-model="mostra_evase"
          dense
          hide-details
          class="mt-0 mr-6"
          color="grey"
          label="Evase"
        />

        <v-text-field
          v-model.number="capacita"
          type="number"
          dense
          outlined
          hide-details
          label="Capacità CC/giorno"
          style="max-width: 160px"
        />
      </v-card-title>

      <v-divider />

      <v-card-text class="pt-2">
        <v-alert
          v-if="giorni_sovraccarichi.length"
          type="warning"
          text
          dense
          class="mb-2"
        >
          {{ giorni_sovraccarichi.length }} giornate oltre la capacità:
          {{ giorni_sovraccarichi.join(" · ") }}
        </v-alert>

        <v-calendar
          ref="calendario"
          v-model="fuoco"
          locale="it-IT"
          :type="tipo"
          :events="eventi"
          :event-more="false"
          :weekdays="[1, 2, 3, 4, 5, 6, 0]"
          :first-interval="7"
          :interval-count="13"
          :event-height="tipo === 'month' ? 22 : 94"
          event-overlap-mode="stack"
          @change="aggiorna_intervallo"
          @mousedown:event="inizia_trascinamento"
          @mouseenter:day="segui_bersaglio"
          @mouseup:day="rilascia_su_giorno"
          @click:event="apri_evento"
        >
          <!-- Anteprima del rilascio: un clone della consegna si inserisce
               fra quelle del giorno, e il carico che ne risulterebbe.
               Vuetify rende lo slot dopo gli eventi, quindi il clone cade
               naturalmente in fondo alla pila. -->
          <template v-slot:day="{ date }">
            <template v-if="bersaglio_valido(date)">
              <div class="fantasma" :class="trascinato.color">
                <v-icon v-if="trascinato.evasa" x-small color="white">
                  mdi-check-bold
                </v-icon>
                <span class="numero_compatto">{{ trascinato.numero }}</span>
                <span class="nome_compatto">{{ trascinato.nome_cliente }}</span>
                <span v-if="trascinato.parti > 1" class="parti_compatte">
                  {{ trascinato.indice }}/{{ trascinato.parti }}
                </span>
              </div>
              <div class="previsione">
                <template v-if="trascinato.ritiro">non incide sulla capacità</template>
                <template v-else>
                  {{ carico(date).toFixed(1) }} →
                  <b>{{ (carico(date) + trascinato.cc).toFixed(1) }}</b> cc
                  <span v-if="sfonderebbe(date)" class="red--text">· oltre capacità</span>
                </template>
              </div>
            </template>
          </template>

          <!-- Carico della giornata: è il numero che decide se ci sta -->
          <template v-slot:day-label="{ date, day, present }">
            <div class="etichetta_giorno">
              <span :class="present ? 'oggi' : ''">{{ day }}</span>
              <span v-if="carico(date)" class="carico" :class="classe_carico(date)">
                {{ carico(date).toFixed(1) }} cc
              </span>
            </div>
            <div v-if="carico(date)" class="barra_giorno">
              <v-progress-linear
                :value="percentuale_carico(date)"
                :color="colore_carico(date)"
                height="5"
                rounded
                background-color="grey lighten-3"
              />
            </div>
          </template>

          <!-- Nel mese conta il carico della giornata, che sta già
               nell'etichetta del giorno: la consegna sta su una riga sola.
               Il dettaglio completo vive nella vista settimana. -->
          <template v-if="tipo === 'month'" v-slot:event="{ event }">
            <div
              class="compatta"
              :class="{
                tratteggiata: event.presunta,
                in_volo: in_trascinamento(event),
              }"
            >
              <v-icon v-if="event.evasa" x-small color="white">mdi-check-bold</v-icon>
              <span class="numero_compatto">{{ event.numero }}</span>
              <span class="nome_compatto">{{ event.nome_cliente }}</span>
              <v-icon v-if="event.ordine_creato" x-small color="white" class="mr-1">
                mdi-clipboard-check
              </v-icon>
              <span v-if="event.parti > 1" class="parti_compatte">
                {{ event.indice }}/{{ event.parti }}
              </span>
            </div>
          </template>

          <template v-else v-slot:event="{ event }">
            <div
              class="scheda"
              :class="{
                tratteggiata: event.presunta,
                in_volo: in_trascinamento(event),
              }"
            >
              <!-- Ritiro e consegna li distingue già il colore dell'evento:
                   qui conta il numero cliente, che va trovato per primo. -->
              <div class="alto">
                <span class="numero_cliente">{{ event.numero }}</span>
                <v-icon v-if="event.ordine_creato" x-small color="white" class="segno">
                  mdi-clipboard-check
                </v-icon>
              </div>
              <div class="nome_cliente">{{ event.nome_cliente }}</div>

              <div class="dettagli">
                <span class="etichetta">Orario:</span>
                <span v-if="event.ora">{{ event.ora }}</span>
                <span v-else class="fioco">da definire</span>
              </div>

              <div class="dettagli">
                <span class="etichetta">Carrelli:</span>
                <span class="cc_evento">{{ event.cc.toFixed(1) }} cc</span>
              </div>

              <div class="dettagli">
                <span class="tag" :class="classe_tag(event)">
                  {{ etichetta_tag(event) }}
                </span>
                <span v-if="event.parti > 1" class="parti">
                  {{ event.indice }}/{{ event.parti }}
                </span>
              </div>
            </div>
          </template>
        </v-calendar>
      </v-card-text>

      <v-card-actions>
        <span class="ml-3 grey--text text-caption">
          Trascina una consegna su un altro giorno per spostarla ·
          {{ eventi.length }} nel periodo ·
          {{ cc_periodo.toFixed(1) }} CC da consegnare, ritiri esclusi
        </span>
        <v-spacer />
        <v-btn text color="grey" @click="aperto = false">Chiudi</v-btn>
      </v-card-actions>
    </v-card>

    <!-- Dettaglio rapido dell'evento -->
    <v-menu
      v-model="popup"
      :activator="elemento_attivo"
      :close-on-content-click="false"
      offset-x
    >
      <v-card v-if="evento_attivo" width="380">
        <v-toolbar :color="evento_attivo.color" dark dense flat>
          <v-toolbar-title class="text-subtitle-1">
            <b>{{ evento_attivo.numero }}</b> {{ evento_attivo.nome_cliente }}
          </v-toolbar-title>
        </v-toolbar>

        <v-card-text class="pt-4">
          <div class="text-subtitle-1 black--text">
            {{ data_estesa(evento_attivo) }}
            <span class="grey--text">·</span>
            {{ evento_attivo.cc.toFixed(1) }} cc
            <span v-if="evento_attivo.presunta" class="orange--text text--darken-2">
              · presunta
            </span>
          </div>

          <!-- Il peso sulla giornata: è il dato che dice se va spostata -->
          <div class="mt-4 d-flex align-center">
            <span class="grey--text text-caption">Carico del giorno</span>
            <v-spacer />
            <span class="text-caption" :class="classe_carico(evento_attivo.giorno)">
              {{ carico(evento_attivo.giorno).toFixed(1) }} / {{ capacita }} cc
            </span>
          </div>
          <v-progress-linear
            :value="percentuale_carico(evento_attivo.giorno)"
            :color="colore_carico(evento_attivo.giorno)"
            height="6"
            rounded
            background-color="grey lighten-3"
          />
          <div v-if="evento_attivo.ritiro" class="text-caption grey--text mt-1">
            Il ritiro non pesa sulla capacità dei mezzi
          </div>
          <div v-else-if="quota_sul_giorno" class="text-caption grey--text mt-1">
            Questa consegna ne è il {{ quota_sul_giorno }}%
          </div>

          <!-- Le altre consegne della stessa prenotazione -->
          <template v-if="sorelle.length">
            <div class="grey--text text-caption mt-4">
              Consegna {{ evento_attivo.indice }} di {{ evento_attivo.parti }} ·
              le altre
            </div>
            <div
              v-for="sorella in sorelle"
              :key="sorella.id"
              class="text-caption d-flex"
            >
              <span>{{ sorella.data }}</span>
              <v-spacer />
              <span>{{ sorella.cc.toFixed(1) }} cc</span>
            </div>
          </template>

          <v-alert
            v-if="ripartizione_attiva.mancante > 0"
            type="warning"
            text
            dense
            class="mt-3 mb-0 text-caption"
          >
            {{ ripartizione_attiva.mancante }} pezzi non ancora ripartiti
          </v-alert>

          <div v-if="nota_attiva" class="mt-3 text-caption grey--text font-italic">
            <v-icon x-small color="amber darken-2" class="mr-1">mdi-note-text</v-icon>
            {{ nota_attiva }}
          </div>

          <v-divider class="my-3" />

          <div class="grey--text text-caption mb-1">Sposta a</div>
          <v-row no-gutters>
            <v-col v-for="ora in ore_disponibili" :key="ora" cols="4" class="pa-1">
              <v-btn
                block
                small
                outlined
                :color="ora === ora_attuale ? 'green' : 'grey'"
                @click="cambia_ora(ora)"
              >
                {{ ora }}:00
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions>
          <v-btn text small color="green" @click="apri_prenotazione">
            <v-icon left small>mdi-open-in-new</v-icon>Apri prenotazione
          </v-btn>
          <v-spacer />
          <v-btn text small color="grey" @click="popup = false">Chiudi</v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>

    <v-snackbar v-model="avviso" color="red" text outlined top timeout="4000">
      <v-icon color="red" class="mr-2">mdi-alert</v-icon>{{ messaggio }}
    </v-snackbar>
  </v-dialog>
</template>

<script>
import moment from "moment";

const PREF_CAPACITA = "cris_capacita_cc";
// Fasce in cui si carica e si consegna.
const ORE = [8, 10, 12, 14, 16, 18];

/**
 * Calendario delle consegne crisantemi.
 *
 * È costruito attorno alla capacità, non all'elenco: ogni giornata mostra i
 * carrelli impegnati rispetto a quelli disponibili, perché è quello il
 * vincolo che decide se una data regge. Lo spostamento avviene trascinando,
 * non aprendo un menu e scegliendo una data da un picker.
 *
 * Gli eventi sono le consegne programmate; le prenotazioni che non ne hanno
 * ancora compaiono alla loro data presunta, tratteggiate, perché sono
 * proprio quelle da pianificare.
 */
export default {
  props: {
    anno: { type: String, required: true },
  },

  data() {
    return {
      aperto: false,
      tipo: "month",
      fuoco: "",
      mostra_consegne: true,
      mostra_ritiri: true,
      mostra_evase: true,
      capacita: Number(localStorage.getItem(PREF_CAPACITA)) || 60,
      intervallo: { start: null, end: null },
      trascinato: null,
      giorno_bersaglio: null,
      appena_spostato: false,
      popup: false,
      elemento_attivo: null,
      evento_attivo: null,
      avviso: false,
      messaggio: "",
      titolo: "",
    };
  },

  computed: {
    prenotazioni() {
      return this.$store.getters["crisantemi/tutte"];
    },

    eventi() {
      const lista = [];

      this.prenotazioni
        .filter((p) => this.mostra_evase || !p.evasa)
        .filter((p) => (p.ritiro ? this.mostra_ritiri : this.mostra_consegne))
        .forEach((p) => {
          const cliente = this.$store.getters.get_cliente_by_iva(p.cliente);
          const numero = cliente ? cliente.numero : "—";
          const nome_cliente = cliente ? cliente.nome : p.cliente;
          const nome = `${numero} ${nome_cliente}`;
          // L'evasa perde la distinzione ritiro/consegna: quello che conta
          // è che è chiusa.
          const colore = p.evasa
            ? "grey"
            : p.ritiro
            ? "green"
            : "blue";

          if (!p.consegne.length) {
            if (!p.ts_consegna_presunta) return;
            lista.push(
              this.costruisci_evento({
                nome,
                numero,
                nome_cliente,
                ts: p.ts_consegna_presunta,
                colore: p.evasa
                  ? "grey"
                  : p.ritiro
                  ? "green lighten-2"
                  : "blue lighten-2",
                id_prenotazione: p.id,
                id_consegna: null,
                ritiro: p.ritiro,
                cc: this.$store.getters["crisantemi/cc"]({
                  id_prenotazione: p.id,
                }),
                presunta: true,
                evasa: p.evasa,
                ordine_creato: false,
                indice: 1,
                parti: 1,
              })
            );
            return;
          }

          p.consegne.forEach((c, indice) => {
            if (!c.ts_data) return;
            lista.push(
              this.costruisci_evento({
                nome:
                  p.consegne.length > 1
                    ? `${nome} ${indice + 1}/${p.consegne.length}`
                    : nome,
                numero,
                nome_cliente,
                ts: c.ts_data,
                colore,
                id_prenotazione: p.id,
                id_consegna: c.id,
                ritiro: p.ritiro,
                cc: this.$store.getters["crisantemi/cc"]({
                  id_prenotazione: p.id,
                  id_consegna: c.id,
                }),
                presunta: false,
                evasa: p.evasa,
                ordine_creato: !!c.id_ordine_creato,
                indice: indice + 1,
                parti: p.consegne.length,
              })
            );
          });
        });

      return lista;
    },

    /**
     * Carrelli per giorno, indicizzati per data ISO.
     *
     * Contano solo le consegne: la capacità è quella dei mezzi, e i ritiri
     * se li porta via il cliente senza occupare un camion.
     */
    carico_per_giorno() {
      const mappa = {};
      this.eventi
        .filter((e) => !e.ritiro)
        .forEach((e) => {
          mappa[e.giorno] = (mappa[e.giorno] || 0) + e.cc;
        });
      return mappa;
    },

    giorni_sovraccarichi() {
      if (!this.capacita) return [];
      return Object.keys(this.carico_per_giorno)
        .filter((giorno) => this.dentro_intervallo(giorno))
        .filter((giorno) => this.carico_per_giorno[giorno] > this.capacita)
        .sort()
        .map((giorno) => moment(giorno).format("D MMM"));
    },

    cc_periodo() {
      return Object.keys(this.carico_per_giorno)
        .filter((giorno) => this.dentro_intervallo(giorno))
        .reduce((tot, giorno) => tot + this.carico_per_giorno[giorno], 0);
    },

    ore_disponibili() {
      return ORE;
    },

    ora_attuale() {
      if (!this.evento_attivo) return null;
      return Number(moment.unix(this.evento_attivo.ts).format("H"));
    },

    prenotazione_attiva() {
      if (!this.evento_attivo) return null;
      return this.$store.getters["crisantemi/by_id"](
        this.evento_attivo.id_prenotazione
      );
    },

    /**
     * Peso di questa consegna sul carico della giornata. I ritiri non
     * entrano nel carico, quindi non hanno una quota da mostrare.
     */
    quota_sul_giorno() {
      if (!this.evento_attivo || this.evento_attivo.ritiro) return null;
      const totale = this.carico(this.evento_attivo.giorno);
      if (!totale || totale === this.evento_attivo.cc) return null;
      return Math.round((this.evento_attivo.cc / totale) * 100);
    },

    /**
     * Le altre consegne della stessa prenotazione: spostandone una si vuole
     * sapere dove sono le sorelle, altrimenti si finisce per accavallarle.
     */
    sorelle() {
      const pren = this.prenotazione_attiva;
      if (!pren || !this.evento_attivo.id_consegna) return [];
      return (pren.consegne || [])
        .filter((c) => c.id !== this.evento_attivo.id_consegna && c.ts_data)
        .sort((a, b) => a.ts_data - b.ts_data)
        .map((c) => ({
          id: c.id,
          data: moment.unix(c.ts_data).format("ddd D MMM · HH:mm"),
          cc: this.$store.getters["crisantemi/cc"]({
            id_prenotazione: pren.id,
            id_consegna: c.id,
          }),
        }));
    },

    ripartizione_attiva() {
      if (!this.evento_attivo) return { mancante: 0 };
      return this.$store.getters["crisantemi/ripartizione"](
        this.evento_attivo.id_prenotazione
      );
    },

    nota_attiva() {
      return this.prenotazione_attiva ? this.prenotazione_attiva.nota : null;
    },
  },

  watch: {
    capacita(valore) {
      localStorage.setItem(PREF_CAPACITA, valore);
    },

    aperto(adesso) {
      if (!adesso) return;
      // Il calendario misura le celle al montaggio: se il dialog non è
      // ancora a schermo le trova a zero e disegna storto.
      this.$nextTick(() => {
        setTimeout(() => {
          if (this.$refs.calendario) this.$refs.calendario.checkChange();
        }, 100);
      });
    },
  },

  mounted() {
    // Si apre sulla campagna dell'anno scelto, non sul mese corrente.
    this.fuoco = `${this.anno}-10-15`;
    window.addEventListener("mouseup", this.annulla_trascinamento);
  },

  beforeDestroy() {
    window.removeEventListener("mouseup", this.annulla_trascinamento);
  },

  methods: {
    costruisci_evento({
      nome,
      numero,
      nome_cliente,
      ts,
      colore,
      id_prenotazione,
      id_consegna,
      ritiro,
      cc,
      presunta,
      evasa,
      ordine_creato,
      indice,
      parti,
    }) {
      const momento = moment.unix(ts);
      const con_ora = momento.format("HH:mm") !== "00:00";
      return {
        name: nome,
        numero,
        nome_cliente,
        ora: con_ora ? momento.format("HH:mm") : null,
        ordine_creato,
        indice,
        parti,
        start: con_ora
          ? momento.format("YYYY-MM-DD HH:mm")
          : momento.format("YYYY-MM-DD"),
        end: con_ora
          ? momento.clone().add(2, "hours").format("YYYY-MM-DD HH:mm")
          : momento.format("YYYY-MM-DD"),
        timed: con_ora,
        color: colore,
        evasa,
        // Gli eventi si ricostruiscono a ogni ricalcolo: per riconoscere
        // quello in trascinamento serve una chiave, non l'identità.
        chiave: `${id_prenotazione}:${id_consegna || "presunta"}`,
        giorno: momento.format("YYYY-MM-DD"),
        ts,
        id_prenotazione,
        id_consegna,
        ritiro,
        cc,
        presunta,
      };
    },

    aggiorna_intervallo({ start, end }) {
      this.intervallo = { start: start.date, end: end.date };
      this.titolo = this.$refs.calendario ? this.$refs.calendario.title : "";
    },

    dentro_intervallo(giorno) {
      if (!this.intervallo.start) return true;
      return giorno >= this.intervallo.start && giorno <= this.intervallo.end;
    },

    carico(giorno) {
      return this.carico_per_giorno[giorno] || 0;
    },

    percentuale_carico(giorno) {
      if (!this.capacita) return 0;
      return Math.min(100, (this.carico(giorno) / this.capacita) * 100);
    },

    colore_carico(giorno) {
      if (!this.capacita) return "grey";
      const rapporto = this.carico(giorno) / this.capacita;
      if (rapporto > 1) return "red";
      if (rapporto > 0.85) return "orange";
      return "green";
    },

    classe_carico(giorno) {
      const colore = this.colore_carico(giorno);
      return colore === "red"
        ? "red--text"
        : colore === "orange"
        ? "orange--text text--darken-2"
        : "grey--text";
    },

    /** Evasa vince su tutto: a campagna chiusa il resto non serve più. */
    etichetta_tag(evento) {
      if (evento.evasa) return "evasa";
      return evento.presunta ? "presunta" : "confermata";
    },

    classe_tag(evento) {
      if (evento.evasa) return "tag_evasa";
      return evento.presunta ? "tag_presunta" : "tag_confermata";
    },

    oggi() {
      this.fuoco = moment().format("YYYY-MM-DD");
    },

    /* --- Spostamento --------------------------------------------------- */

    inizia_trascinamento({ event }) {
      this.trascinato = event;
      this.giorno_bersaglio = null;
    },

    segui_bersaglio({ date }) {
      if (!this.trascinato) return;
      this.giorno_bersaglio = date;
    },

    in_trascinamento(evento) {
      return !!this.trascinato && this.trascinato.chiave === evento.chiave;
    },

    bersaglio_valido(date) {
      return (
        this.tipo === "month" &&
        this.trascinato &&
        date === this.giorno_bersaglio &&
        date !== this.trascinato.giorno
      );
    },

    sfonderebbe(date) {
      if (!this.capacita || !this.trascinato || this.trascinato.ritiro) return false;
      return this.carico(date) + this.trascinato.cc > this.capacita;
    },

    /**
     * Rilascio fuori dal calendario: senza questa rete il trascinamento
     * resterebbe armato e il primo click su un giorno sposterebbe la
     * consegna senza che nessuno l'abbia chiesto.
     */
    annulla_trascinamento() {
      this.trascinato = null;
      this.giorno_bersaglio = null;
    },

    rilascia_su_giorno({ date }) {
      const evento = this.trascinato;
      this.trascinato = null;
      this.giorno_bersaglio = null;
      if (!evento || date === evento.giorno) return;

      // L'ora si conserva: si sposta la giornata, non la fascia.
      const ora = moment.unix(evento.ts).format("HH:mm:ss");
      const ts = moment(`${date} ${ora}`).format("X");

      this.appena_spostato = true;
      this.popup = false;
      this.sposta(evento, ts).finally(() => {
        setTimeout(() => {
          this.appena_spostato = false;
        }, 100);
      });
    },

    sposta(evento, ts) {
      // Una prenotazione senza consegne si sposta cambiando la data presunta.
      if (!evento.id_consegna) {
        return this.$store
          .dispatch("crisantemi/imposta_data_presunta", {
            id: evento.id_prenotazione,
            ts,
          })
          .catch((err) => this.segnala("Spostamento non riuscito", err));
      }
      return this.$store
        .dispatch("crisantemi/cambia_data_consegna", {
          id_prenotazione: evento.id_prenotazione,
          id_consegna: evento.id_consegna,
          ts,
        })
        .catch((err) => this.segnala("Spostamento non riuscito", err));
    },

    cambia_ora(ora) {
      const evento = this.evento_attivo;
      const giorno = moment.unix(evento.ts).format("YYYY-MM-DD");
      const ts = moment(`${giorno} ${String(ora).padStart(2, "0")}:00`).format("X");
      this.popup = false;
      this.sposta(evento, ts);
    },

    /* --- Popup --------------------------------------------------------- */

    apri_evento({ nativeEvent, event }) {
      // Dopo un trascinamento il click residuo non deve aprire il popup.
      if (this.appena_spostato) return;
      const mostra = () => {
        this.evento_attivo = event;
        this.elemento_attivo = nativeEvent.target;
        requestAnimationFrame(() =>
          requestAnimationFrame(() => (this.popup = true))
        );
      };
      if (this.popup) {
        this.popup = false;
        requestAnimationFrame(() => requestAnimationFrame(mostra));
      } else {
        mostra();
      }
      nativeEvent.stopPropagation();
    },

    data_estesa(evento) {
      const m = moment.unix(evento.ts);
      return m.format("HH:mm") === "00:00"
        ? m.format("ddd D MMMM")
        : m.format("ddd D MMMM · HH:mm");
    },

    apri_prenotazione() {
      this.popup = false;
      this.$emit("apri", this.evento_attivo.id_prenotazione);
    },

    segnala(cosa, err) {
      const stato = err && err.response ? err.response.status : null;
      this.messaggio = stato ? `${cosa}: errore ${stato}.` : `${cosa}.`;
      this.avviso = true;
    },
  },
};
</script>

<style scoped>
.etichetta_giorno {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding: 2px 6px 0;
}

.oggi {
  font-weight: 700;
  color: #1976d2;
}

.carico {
  font-size: 0.68rem;
  font-variant-numeric: tabular-nums;
}

/* Rientro pari a quello dell'etichetta del giorno: la barra non deve
   toccare i bordi della cella, altrimenti si legge come un separatore. */
.barra_giorno {
  padding: 0 6px 2px;
}

/* Scheda dell'evento: il numero cliente è il primo aggancio per l'occhio,
   il resto scende in ordine di dettaglio decrescente. */
.scheda {
  height: 100%;
  padding: 5px 7px;
  overflow: hidden;
  color: #fff;
  /* Le righe si distribuiscono sull'altezza disponibile invece di
     accatastarsi in alto: l'interlinea segue lo spazio che c'è. */
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  line-height: 1.35;
}

.tratteggiata {
  border: 1px dashed rgba(255, 255, 255, 0.85);
  border-radius: 3px;
}

.alto {
  display: flex;
  align-items: center;
}

.numero_cliente {
  font-size: 1rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}

.segno {
  margin-left: auto;
}

.nome_cliente {
  font-size: 0.74rem;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dettagli {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.68rem;
  font-variant-numeric: tabular-nums;
}

.etichetta {
  opacity: 0.65;
}

.fioco {
  opacity: 0.6;
  font-style: italic;
}

.cc_evento {
  font-weight: 600;
}

.parti {
  margin-left: auto;
  padding: 0 4px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.22);
}

/* Stato della data: presunta finché non esiste una consegna programmata.
   Testo nudo, non pillola: sulla riga resta allineato al badge delle parti
   invece di sollevarlo con padding e sfondo. */
.tag {
  font-size: 0.62rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.tag_confermata {
  opacity: 0.7;
}

/* La presunta resta marcata: è quella su cui c'è ancora da lavorare. */
.tag_presunta {
  font-weight: 700;
}

.tag_evasa {
  font-weight: 700;
}

/* La consegna che si sta trascinando resta al suo posto ma si smaterializza,
   così è chiaro che sta per essere spostata altrove. */
.in_volo {
  opacity: 0.4;
  filter: grayscale(0.5);
}

/* Clone provvisorio nella casella di destinazione: stessa forma e stesso
   colore dell'evento reale, ma smaterializzato e col bordo tratteggiato,
   perché è un'anteprima e non un dato. Misure allineate a .v-event del
   mese, così si incolonna con le consegne già presenti. */
.fantasma {
  display: flex;
  align-items: center;
  gap: 5px;
  margin: 0 6px 2px;
  padding: 0 5px;
  height: 22px;
  border-radius: 4px;
  border: 1px dashed rgba(255, 255, 255, 0.9);
  opacity: 0.75;
  color: #fff;
  font-size: 0.72rem;
  overflow: hidden;
  white-space: nowrap;
  pointer-events: none;
}

.previsione {
  margin: 0 6px;
  font-size: 0.68rem;
  font-variant-numeric: tabular-nums;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
  pointer-events: none;
}

/* Riga compatta del mese: numero, nome, quale consegna. */
.compatta {
  display: flex;
  align-items: center;
  gap: 5px;
  height: 100%;
  padding: 0 5px;
  color: #fff;
  font-size: 0.72rem;
  overflow: hidden;
  white-space: nowrap;
}

.numero_compatto {
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

.nome_compatto {
  overflow: hidden;
  text-overflow: ellipsis;
}

.parti_compatte {
  margin-left: auto;
  padding: 0 3px;
  border-radius: 2px;
  background: rgba(255, 255, 255, 0.22);
  font-variant-numeric: tabular-nums;
}

/* Le righe del mese seguono il contenuto invece di spartirsi l'altezza
   disponibile: una giornata con sei consegne le mostra tutte, e la massa
   visiva delle settimane dice da sola dove si concentra la campagna. */
.v-calendar-monthly {
  height: auto;
}

.v-calendar-monthly >>> .v-calendar-weekly__week {
  flex: 0 0 auto;
}

.v-calendar >>> .v-calendar-weekly__day {
  min-height: 130px;
  height: auto;
  overflow: visible;
}

/* Solo la vista mese: in settimana l'altezza degli eventi la calcola
   Vuetify sulla durata, e forzarla qui la annullerebbe. Il margine
   simmetrico corregge il default, che lascia spazio solo a destra. */
.v-calendar-monthly >>> .v-event {
  height: auto !important;
  width: auto !important;
  margin: 0 6px 3px;
  border-radius: 4px;
  cursor: grab;
}

.v-calendar-monthly >>> .v-event:active {
  cursor: grabbing;
}
</style>
